import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PaymentStep from '../components/PaymentStep';
import { CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * /pay/:token
 *
 * Public page the customer lands on after clicking the deposit link
 * emailed to them by `send_deposit_link_email`. Reuses `PaymentStep`
 * (same component the self-serve wizard uses) so the Stripe Elements
 * UX is identical.
 *
 * Flow:
 *   1. Fetch booking details via /api/email/booking-details/:token
 *   2. If deposit already paid → show "already confirmed" state
 *   3. Otherwise render <PaymentStep /> with booking props
 *   4. On success → confirm payment via /api/stripe/payment/confirm,
 *      show "thanks" state.
 */
const PayDeposit = () => {
  const { token } = useParams();
  const [state, setState] = useState('loading'); // loading | ready | paid | expired | error | success
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND}/api/email/booking-details/${token}`);
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (res.status === 404) { setState('expired'); return; }
          setError(body.detail || 'Could not load booking'); setState('error'); return;
        }
        const b = body.booking;
        setBooking(b);
        if (b.deposit_paid || b.payment_status === 'succeeded') {
          setState('paid');
        } else {
          setState('ready');
        }
      } catch (e) {
        setError('Could not load booking'); setState('error');
      }
    })();
  }, [token]);

  const handlePaymentSuccess = async () => {
    try {
      await fetch(`${BACKEND}/api/stripe/payment/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_id: booking.id }),
      });
    } catch (_) { /* backend webhook also confirms — safe to ignore */ }
    setState('success');
  };

  return (
    <div style={pageStyle} data-testid="pay-deposit-page">
      <div style={cardStyle}>
        {state === 'loading' && (
          <Centered><Loader2 size={28} className="spin" color="#64748b" />
            <p>Loading your booking…</p></Centered>
        )}
        {state === 'expired' && (
          <Centered><AlertTriangle size={32} color="#f59e0b" />
            <h3>Link expired</h3>
            <p>This deposit link has expired or isn't valid any more. Please contact the shop to re-send one.</p></Centered>
        )}
        {state === 'error' && (
          <Centered><AlertTriangle size={32} color="#ef4444" />
            <h3>Something went wrong</h3>
            <p>{error || 'Please try the link in your email again, or contact the shop.'}</p></Centered>
        )}
        {state === 'paid' && booking && (
          <Centered><CheckCircle2 size={34} color="#10b981" />
            <h3>Deposit already paid</h3>
            <p>Your booking at <b>{booking.shop_name}</b> is confirmed — see you soon.</p>
            <BookingSummary b={booking} /></Centered>
        )}
        {state === 'success' && booking && (
          <Centered><CheckCircle2 size={34} color="#10b981" />
            <h3>Deposit received</h3>
            <p>Thanks! Your booking at <b>{booking.shop_name}</b> is confirmed.</p>
            <BookingSummary b={booking} /></Centered>
        )}
        {state === 'ready' && booking && (
          <div data-testid="pay-deposit-ready">
            <div style={{ marginBottom: 20, textAlign: 'center' }}>
              <h2 style={{ margin: '0 0 6px', fontSize: 22 }}>Confirm your booking</h2>
              <p style={{ color: '#64748b', margin: 0, fontSize: 14 }}>
                Pay the deposit to secure your slot at <b>{booking.shop_name}</b>.
              </p>
            </div>
            <BookingSummary b={booking} />
            <PaymentStep
              booking={{
                bookingId:      booking.id,
                depositAmount:  Number(booking.deposit_amount || 0),
                fullAmount:     Number(booking.service_price || 0),
                allowPayInFull: !!booking.allow_pay_in_full,
                serviceName:    booking.service_name,
                barberName:     booking.barber_name,
                time:           new Date(booking.starts_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }),
              }}
              onSuccess={handlePaymentSuccess}
              onBack={() => { /* no back from email link */ }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const Centered = ({ children }) => (
  <div style={{ textAlign: 'center', padding: '18px 10px' }}>
    {children}
  </div>
);

const BookingSummary = ({ b }) => (
  <div style={{
    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
    padding: '14px 16px', margin: '14px 0', fontSize: 14,
  }}>
    <Row k="Service" v={b.service_name} />
    <Row k="Barber"  v={b.barber_name} />
    <Row k="When"    v={new Date(b.starts_at).toLocaleString('en-GB', { dateStyle: 'full', timeStyle: 'short' })} />
    {Number(b.deposit_amount) > 0 && <Row k="Deposit" v={`£${Number(b.deposit_amount).toFixed(2)}`} />}
  </div>
);
const Row = ({ k, v }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
    <span style={{ color: '#64748b' }}>{k}</span>
    <span style={{ fontWeight: 500 }}>{v}</span>
  </div>
);

const pageStyle = {
  minHeight: '100vh', background: '#f1f5f9',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  padding: '40px 16px',
};
const cardStyle = {
  width: '100%', maxWidth: 560, background: '#fff',
  border: '1px solid #e2e8f0', borderRadius: 14,
  padding: 28, boxShadow: '0 1px 2px rgba(0,0,0,.04)',
};

export default PayDeposit;
